/**
 * A simple pub/sub service that allows the Slides iframe to register
 * a "start present" handler, and the Confluence header Present button
 * to invoke it.
 */
export class ContentPresentationService {
	private handler: (() => void) | null = null;
	private availabilityListeners: Set<(isAvailable: boolean) => void> = new Set();

	/**
	 * Register a handler that starts presentation mode.
	 * Called by the content iframe in the Confluence header
	 *
	 * @returns An unsubscribe function to remove the handler.
	 */
	onStartPresent(cb: () => void): () => void {
		this.handler = cb;
		this.notifyAvailabilityListeners(true);
		return () => {
			if (this.handler === cb) {
				this.handler = null;
				this.notifyAvailabilityListeners(false);
			}
		};
	}

	/**
	 * Trigger presentation mode. Called by the Confluence header Present button.
	 * Invokes the handler registered by the content iframe.
	 */
	startPresent(): void {
		this.handler?.();
	}

	/**
	 * Returns whether a presentation handler is currently registered.
	 */
	get isAvailable(): boolean {
		return this.handler !== null;
	}

	/**
	 * Subscribe to availability changes. Called by UI components
	 * to reactively show/hide the Present button.
	 *
	 * @returns An unsubscribe function.
	 */
	onAvailabilityChange(cb: (isAvailable: boolean) => void): () => void {
		this.availabilityListeners.add(cb);
		return () => {
			this.availabilityListeners.delete(cb);
		};
	}

	private notifyAvailabilityListeners(isAvailable: boolean): void {
		this.availabilityListeners.forEach((listener) => listener(isAvailable));
	}

	dispose(): void {
		this.handler = null;
		this.availabilityListeners.clear();
	}
}

export const contentPresentationService = new ContentPresentationService();
